import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { WIDGET_REGISTRY } from './widgets/registry'
import { layoutScreen, GRID_COLS, GRID_ROWS } from './layout'

const GRID_GAP = 12

// One wallboard screen. Every widget gets an explicit grid cell from
// layoutScreen() — no CSS auto-placement — and every cell wrapper gets an
// exact pixel height with overflow hidden, so a widget whose content renders
// tall (fonts, charts) clips inside its own cell instead of bleeding over the
// row below. Charts additionally need explicit pixel heights (cellPx prop):
// a flexible-height parent makes the chart.js canvas resize-loop.
const WallboardGrid = ({ screen, data }) => {
  const containerRef = useRef(null)
  const [cell, setCell] = useState({ w: 120, h: 80 })

  useEffect(() => {
    const element = containerRef.current
    if (!element) return undefined
    const measure = () => {
      const { clientWidth, clientHeight } = element
      if (clientWidth > 0 && clientHeight > 0) {
        // Both axes pixel-locked — fr tracks would stretch with a widget's
        // min-content width, changing the stage size with its content
        const next = {
          w: Math.max(
            Math.floor((clientWidth - GRID_GAP * (GRID_COLS - 1)) / GRID_COLS),
            20,
          ),
          h: Math.max(
            Math.floor((clientHeight - GRID_GAP * (GRID_ROWS - 1)) / GRID_ROWS),
            20,
          ),
        }
        setCell((current) =>
          current.w === next.w && current.h === next.h ? current : next,
        )
      }
    }
    // First measure after the surrounding layout (top bar, fonts, banners)
    // has settled — a premature read oversizes the stage
    const frame = requestAnimationFrame(measure)
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  const placements = layoutScreen(screen?.widgets ?? [])

  return (
    <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLS}, ${cell.w}px)`,
          gridTemplateRows: `repeat(${GRID_ROWS}, ${cell.h}px)`,
          gap: `${GRID_GAP}px`,
        }}
      >
        {placements.map(({ widget, index, col, row, cols, rows }) => {
          const definition = WIDGET_REGISTRY[widget.type]
          // col === null means the screen is over-full — the editor warns;
          // the display quietly skips what doesn't fit
          if (!definition || col === null) return null
          const heightPx = cell.h * rows + GRID_GAP * (rows - 1)
          const WidgetComponent = definition.component
          return (
            <Box
              key={widget._id ?? widget._uid ?? index}
              sx={{
                gridColumn: `${col + 1} / span ${cols}`,
                gridRow: `${row + 1} / span ${rows}`,
                height: heightPx,
                minWidth: 0,
                overflow: 'hidden',
                // Clip follows the card shape — same radius as the widget
                // Paper and the editor canvas
                borderRadius: 1,
              }}
            >
              <WidgetComponent widget={widget} data={data} cellPx={heightPx} />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default WallboardGrid
